<template>
  <v-menu
    v-model="menu"
    :close-on-content-click="false"
    offset-y
    min-width="auto"
  >
    <template #activator="{ on, attrs }">
      <v-text-field
        :value="displayValue"
        :label="label"
        readonly
        dense
        outlined
        clearable
        hide-details
        v-bind="attrs"
        v-on="on"
        @click:clear="clear"
      >
        <template #prepend-inner>
          <LucideIcon name="calendar" :size="16" class="mt-1" color="#54636f" />
        </template>
      </v-text-field>
    </template>

    <!--
      v-model against LOCAL state, not :value against the prop.

      A range picker is controlled by its value: the first click emits a
      one-element array, and with a one-way :value bind there was nothing to
      write it back to, so the picker's value stayed [] , the day never lit up,
      and @change -- which fires only once two dates are held -- never fired at
      all. The range filter simply could not be set. FilterBar's own date control
      has always bound v-model to a local array, which is why that one worked.
    -->
    <v-date-picker
      v-model="local"
      :range="range"
      :max="max"
      :min="min"
      no-title
      color="primary"
      @change="onChange"
    />
  </v-menu>
</template>

<script>
import fmt from "@/app-js/karrofin/format";

/**
 * A date, or a date range, in a menu.
 *
 * Exists so the three date filters on Offer Management do not each repeat the
 * menu wiring -- and so the range sort happens in exactly one place:
 * v-date-picker hands back [to, from] when the user picks the later day first,
 * and an unsorted pair silently matches nothing.
 */
export default {
  name: "KfDateField",
  props: {
    /** String for a single date, [from, to] for a range. */
    value: { type: [String, Array], default: null },
    label: { type: String, default: "" },
    range: { type: Boolean, default: false },
    /* Bounds, passed straight to v-date-picker. A screen filtering on something
       that has already happened -- a disbursal, a payment -- passes max="today"
       so the calendar cannot offer a day nothing can be recorded on yet. */
    max: { type: String, default: undefined },
    min: { type: String, default: undefined },
  },
  data() {
    return { menu: false, local: this.normalised() };
  },
  watch: {
    /* Follow the parent when it changes the value from outside -- Reset
       clearing the filters, or a stored range being restored on arrival. */
    value: {
      handler() {
        this.local = this.normalised();
      },
      deep: true,
    },
  },
  computed: {
    picked() {
      if (this.range) return Array.isArray(this.value) ? this.value : [];
      return this.value || null;
    },
    displayValue() {
      if (this.range) {
        const v = this.picked;
        if (!v || v.length !== 2) return "";
        return fmt.date(v[0]) + " → " + fmt.date(v[1]);
      }
      return this.value ? fmt.date(this.value) : "";
    },
  },
  methods: {
    /** The prop, in the shape `local` holds: [] / null when unset. */
    normalised() {
      if (this.range) return Array.isArray(this.value) ? this.value.slice() : [];
      return this.value || null;
    },
    onChange(value) {
      if (this.range) {
        if (!value || value.length !== 2) return; // wait for the second click
        const sorted = value.slice().sort();
        this.local = sorted;
        this.menu = false;
        this.$emit("input", sorted);
      } else {
        this.menu = false;
        this.$emit("input", value);
      }
      this.$emit("change");
    },
    clear() {
      this.local = this.range ? [] : null;
      this.$emit("input", this.range ? [] : null);
      this.$emit("change");
    },
  },
};
</script>
